'use client';

import { useScreenDetect } from '@sgcmpteltd/uikit-next';
import { useLatest } from '@sgcmpteltd/uikit-react';
import cx from 'clsx';
import Cookies from 'js-cookie';
import { useRouter, useSearchParams } from 'next/navigation';
import type { ReactElement } from 'react';
import {
  useContext,
  useEffect,
  useRef,
  useState,
  useTransition,
} from 'react';

import { ACTION_STATUS_SUCCESS } from '@/actions';
import { getMyAlertData } from '@/actions/new-car/alert-list/get';
import { getCarListingData } from '@/actions/new-car/car-listing';
import { getCarModelsListingData } from '@/actions/new-car/car-models';
import {
  setNewcarItemsCookies,
} from '@/actions/new-car/set-newcar-items-cookies';
import type {
  CamelCaseCarList,
} from '@/apis/server/new-car/v1/car-listing/types';
import type {
  CamelCaseAdAlert,
  CamelCaseCarAlert,
  CamelCaseResponseData as CamelCaseMyAlert,
} from '@/apis/server/new-car/v1/my-alert';
import { isCamelCaseAdAlert } from '@/apis/server/new-car/v1/my-alert/utils';
import type { NewCarSortType } from '@/apis/server/new-car/v1/types';
import {
  isCategoryType,
  isNewCarFuelType,
} from '@/apis/server/new-car/v1/utils';
import { NewCarBreadcrumb as Breadcrumb } from '@/components/breadcrumb';
import { ComparisonDrawer } from '@/components/comparison-drawer';
import {
  NUM_COMPARISON_MAX_VEHICLES,
} from '@/components/comparison-drawer/contants';
import { DynamicEyeotaScript } from '@/components/dynamic-eyeota-script';
import { Leaderboard } from '@/components/leaderboard';
import { LoadingPlaceholderList } from '@/components/loading-placeholder-list';
import { PageContentContainer } from '@/components/page-content-container';
import {
  ENGINE_CAP_SELECT_OPTION_VALUE_661_TO_1600,
  ENGINE_CAP_SELECT_OPTION_VALUE_1601_TO_2000,
  ENGINE_CAP_SELECT_OPTION_VALUE_2001_TO_3000,
  ENGINE_CAP_SELECT_OPTION_VALUE_LESS_THAN_661,
  ENGINE_CAP_SELECT_OPTION_VALUE_MORE_THAN_3000,
} from '@/components/search-filter/constants';
import type {
  FilterChoice,
  FilterRange,
  FilterSelect,
  FilterValue,
} from '@/components/search-filter/filter-label-list/types';
import {
  isFilterCheckbox,
} from '@/components/search-filter/filter-label-list/utils';
import type {
  CheckboxValue,
} from '@/components/search-filter/filter-modal/input/checkbox-group/types';
import type { Category } from '@/components/search-filter/filter-modal/types';
import { TITLE_HEADING_H3 } from '@/components/section-container/base';
import {
  SECTION_CONTAINER_VARIANT_CONTENT_OVERFLOWED,
  SectionContainer,
} from '@/components/section-container/page';
import { TextLink } from '@/components/text-link';
import { eyeotaPid } from '@/configs/analytics';
import { COOKIE_KEY_COMPARED_SUBMODELS } from '@/configs/cookies';
import {
  newCarListingLeaderboard as dynamicYieldsListingLeaderboard,
} from '@/configs/dynamic-yields/ads/desktop';
import {
  newCarListingLeaderboard as googleAdsListingLeaderboard,
} from '@/configs/google/ads/desktop';
import { SITE_BASE_PATH } from '@/configs/site';
import {
  AUTHORISED_DISTRIBUTORS,
  PARALLEL_IMPORTERS,
} from '@/constants/global';
import { AppContext } from '@/context/app';
import { useFilterParams } from '@/hooks/use-filter-params';
import { useLocalStorage } from '@/hooks/use-localStorage';
import { joinSgcmMsSite } from '@/utils/url/join/public';

import { Pagination } from '../../../../../components/pagination';
import { createEyeotaAJSScriptTargetMapping } from '../../configs';
import { SECTION_TITLE_NEW_CARS_LISTING_PAST_CARS } from '../../constants';
import type {
  CompareItem,
  OnSearch,
  OnSearchData,
  PageSearchParams,
} from '../../types';
import { collectSearchData } from '../../utils';
import { AdInfoList } from './ad-info-list/component';
import { CarMakeInfoList } from './car-make-info-list';
import { breadcrumbList } from './configs';
import {
  LOADING_PLACEHOLDER_LIST_ITEM_COUNT_DESKTOP,
  LOADING_PLACEHOLDER_LIST_ITEM_COUNT_MOBILE,
  NEW_CARS_COMPARE_LIST_LOCAL_STORAGE_KEY,
  NO_SEARCH_RESULT_MESSAGE,
  NO_SEARCH_RESULT_TITLE,
  RESULT_PER_PAGE_NUM_LIMIT,
} from './constants';
import { NoSearchResult } from './no-search-result';
import { PastCarList } from './past-car-list';
import { PastCarDescription } from './past-car-list/description';
import { ResultPerPageDropdown } from './result-per-page-dropdown/component';
import { SearchFilterSection } from './search-filter-section';
import { SearchList } from './search-list';
import { SortBar } from './sort-bar';
import type { OrderBy, SortOrder, SortOrderValue } from './sort-bar/types';
import { SortSelect } from './sort-select';
import styles from './styles.module.scss';
import type { Component } from './types';
import { createQueryString } from './utils';

export const PageContent: Component = ({
  numOfTotal,
  currentPageNum,
  lastPageNum,
  defaultSearchList,
  defaultSearchData,
  brandList,
  dealerList,
  dealerDetail,
  carMakeList,
  pastCarList,
  numOfPastCarsTotal,
  promotions,
  myAlerts = null,
  compareList = null,
  searchTargetMapping,
}) => {
  const [
    compareItems,
    setCompareItems,
  ] = useLocalStorage<CompareItem[]>(
    NEW_CARS_COMPARE_LIST_LOCAL_STORAGE_KEY,
    compareList ?? [],
  );
  const [
    isComparisonDrawerOpen,
    setIsComparisonDrawerOpen,
  ] = useState(false);
  const [
    isComparisonWarningModalOpen,
    setIsComparisonWarningModalOpen,
  ] = useState(false);
  const [
    searchResultList,
    setSearchResultList,
  ] = useState<CamelCaseCarList | null>(defaultSearchList);
  const [
    query,
    setQuery,
  ] = useState(defaultSearchData.query.q ?? '');
  const [carMakeValue, setCarMakeValue] = useState(
    defaultSearchData.query.make ?? '',
  );
  const adAlertInit = myAlerts?.adAlerts
    .find(alert => alert.make === dealerDetail?.brand) || null;
  const [adAlert, setAdAlert] = useState<CamelCaseAdAlert | null>(adAlertInit);
  const [
    myAlert,
    setMyAlert,
  ] = useState<CamelCaseMyAlert | null>(myAlerts);
  const hasInitialized = useRef(false);
  const [
    numOfTotalCars,
    setNumOfTotalCars,
  ] = useState<number | null>(numOfTotal);
  const [currentPage, setCurrentPage] = useState<number | null>(currentPageNum);
  const [lastPage, setLastPage] = useState<number | null>(lastPageNum);
  const [isSearchPending, startSearchTransition] = useTransition();
  const appContextData = useContext(AppContext);
  const [sorter, setSorter] = useState<SortOrder>('');
  const [
    resultPerPage,
    setResultPerPage,
  ] = useState(defaultSearchData.query.limit ?? RESULT_PER_PAGE_NUM_LIMIT);
  const [
    leaderboardRenderKey,
    setLeaderboardRenderKey,
  ] = useState(0);
  const router = useRouter();
  const searchParams = useSearchParams();
  const { isDesktop } = useScreenDetect();
  const isScreenDesktop = isDesktop();
  const [filterValues] = useFilterParams();
  const containerSearchFilterSectionRef = useRef<HTMLDivElement>(null);
  const { ref: onSearchCarCardRef } = useLatest<OnSearch>({
    value: async (data) => {
      const {
        query: searchQuery,
      } = data;

      const formData = new FormData();

      const searchQueryEntries = Object.entries(searchQuery);

      for (const entry of searchQueryEntries) {
        const [searchQueryKey, searchQueryValue] = entry;

        if (searchQueryValue !== undefined) {
          if (formData.has(searchQueryKey)) {
            formData.set(searchQueryKey, `${searchQueryValue}`);
          } else {
            formData.append(searchQueryKey, `${searchQueryValue}`);
          }
        }
      }

      if (!hasInitialized.current) {
        return;
      }

      const actionResult = await getCarListingData(formData);

      if (actionResult.status !== ACTION_STATUS_SUCCESS) {
        setSearchResultList(null);

        return;
      }

      const {
        data: {
          data: carListingData,
          meta: {
            total,
            currentPage: current,
            lastPage: last,
          },
        },
      } = actionResult;

      setSearchResultList(carListingData);
      setNumOfTotalCars(total);
      setCurrentPage(current);
      setLastPage(last);
    },
  });
  const { ref: onSearchTheOthersRef } = useLatest<OnSearch>({
    value: async (data) => {
      const {
        query: searchQuery,
      } = data;

      if (searchQuery.sortBy && searchQuery.order) {
        setSorter(`${searchQuery.sortBy},${searchQuery.order}`);
      } else {
        setSorter('');
      }

      const formData = new FormData();

      const searchQueryEntries = Object.entries(searchQuery);

      for (const entry of searchQueryEntries) {
        const [searchQueryKey, searchQueryValue] = entry;

        if (searchQueryValue !== undefined) {
          if (formData.has(searchQueryKey)) {
            formData.set(searchQueryKey, `${searchQueryValue}`);
          } else {
            formData.append(searchQueryKey, `${searchQueryValue}`);
          }
        }
      }

      if (searchQuery.q) {
        setQuery(searchQuery.q);
      }

      if (searchQuery.make) {
        setCarMakeValue(searchQuery.make);
      } else {
        setCarMakeValue('');
      }
    },
  });

  useEffect(() => {
    if (myAlert === null) {
      getMyAlertData().then((result) => {
        if (result.status === ACTION_STATUS_SUCCESS) {
          setMyAlert(result.data.data);
        }
      });
    }
  }, [myAlert]);

  useEffect(() => {
    const fetchAndSetCompareList = async (subModelsIdList: number[]) => {
      const actionResults = await Promise.all(
        subModelsIdList.map((subModelId) => {
          const formData = new FormData();

          formData.set('subModelId', subModelId.toString());
          return getCarModelsListingData(formData);
        }),
      );

      const tempCompareList: CompareItem[] = actionResults.map(
        (actionResult, i) => {
          if (actionResult.status === ACTION_STATUS_SUCCESS) {
            const { data } = actionResult.data;
            return {
              id: data[0].id,
              submodelId: subModelsIdList[i],
              make: data[0].make,
              model: data[0].model,
              name: data[0].sub_models.find(
                subModel => subModel.id === subModelsIdList[i],
              )?.name ?? '',
              thumbnailUrl: `https://i.i-sgcm.com/new_cars/cars/${data[0].id}/${data[0].id}_m.jpg`,
            };
          }
          return {
            id: 0,
            submodelId: 0,
            make: '',
            model: '',
            name: '',
            thumbnailUrl: '',
          };
        },
      );

      setCompareItems(tempCompareList);
    };

    const submodels = Cookies.get(COOKIE_KEY_COMPARED_SUBMODELS);

    if (submodels !== undefined) {
      if (submodels === '') {
        setCompareItems([]);
        return;
      }

      const subModelsIdList = decodeURIComponent(submodels)
        .split(',')
        .map(str => Number.parseInt(str, 10));

      fetchAndSetCompareList(subModelsIdList);
    }
  // only run once at init
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const initialize = async () => {
      await onSearchCarCardRef.current?.(defaultSearchData);
      await onSearchTheOthersRef.current?.(defaultSearchData);
      hasInitialized.current = true;
    };

    const search = async () => {
      const paramsRecord: PageSearchParams = {};

      searchParams.forEach((value, key) => {
        paramsRecord[key] = value;
      });

      const searchData = collectSearchData({ searchParams: paramsRecord });

      startSearchTransition(async () => {
        await onSearchCarCardRef.current?.(searchData);
      });
      await onSearchTheOthersRef.current?.(searchData);
    };

    if (hasInitialized.current) {
      search();
    } else {
      initialize();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  const handleComparedCheckboxChange = async (
    compareItem: CompareItem,
    isCompared: boolean,
  ) => {
    const formData = new FormData();

    let newCompareItems: CompareItem[];

    if (isCompared) {
      newCompareItems = [...compareItems, compareItem];
      setIsComparisonDrawerOpen(true);
    } else {
      newCompareItems = compareItems.filter(
        item => item.submodelId !== compareItem.submodelId,
      );

      if (newCompareItems.length === 0) {
        setIsComparisonDrawerOpen(false);
      }
    }

    if (newCompareItems.length > NUM_COMPARISON_MAX_VEHICLES) {
      setIsComparisonWarningModalOpen(true);
      return;
    }

    formData.set(
      'submodelIds',
      newCompareItems
        .map(item => item.submodelId)
        .join(','),
    );

    formData.set(
      'modelIds',
      newCompareItems
        .map(item => item.id)
        .join(','),
    );

    setCompareItems(newCompareItems);

    await setNewcarItemsCookies(formData);
  };

  const hasSearchResult = searchResultList && searchResultList?.length > 0;
  const hasPastCarResult = pastCarList && pastCarList?.length > 0;

  let pageContent: ReactElement | null;

  const handleFilterClick = () => {
    appContextData?.setIsFilterModalOpen(true);
  };

  const handleSortChange = (sorterOrderValue: SortOrderValue) => {
    const searchData = sorterOrderValue.value === ''
      ? {
        query: {
          ...defaultSearchData.query,
          sortBy: undefined,
          order: undefined,
          page: undefined,
        },
      }
      : {
        query: {
          ...defaultSearchData.query,
          sortBy: sorterOrderValue.isChecked
            ? sorterOrderValue.value.split(',')[0] as NewCarSortType
            : undefined,
          order: sorterOrderValue.isChecked
            ? sorterOrderValue.value.split(',')[1] as OrderBy
            : undefined,
          page: undefined,
        },
      };

    const search = createQueryString(searchData);

    setLeaderboardRenderKey(prev => prev + 1);
    router.push(`/listing/search?${search}`);
  };

  if (isSearchPending) {
    pageContent = (
      <div
        className={styles.containerLoadingPlaceholderList}>
        <LoadingPlaceholderList
          itemContainerClassName={styles.containerLoadingPlaceholderListItem}
          itemCount={
            isScreenDesktop
              ? LOADING_PLACEHOLDER_LIST_ITEM_COUNT_DESKTOP
              : LOADING_PLACEHOLDER_LIST_ITEM_COUNT_MOBILE
          } />
      </div>
    );
  } else if (hasSearchResult) {
    pageContent = (
      <PageContentContainer>
        <SearchList
          compareItems={compareItems}
          defaultSearchData={defaultSearchData}
          list={searchResultList}
          myAlertList={myAlert}
          promotionList={promotions}
          searchTargetMapping={searchTargetMapping}
          setMyAlert={setMyAlert}
          onComparedCheckboxChange={handleComparedCheckboxChange} />
        <div className={styles.bottomSortBarContainer}>
          <SortBar
            isSearchPending={isSearchPending}
            isTopRounded={false}
            numberOfCars={numOfTotalCars}
            numberOfFilter={filterValues.length}
            sorter={sorter}
            onFilterClick={handleFilterClick}
            onSortChange={handleSortChange} />
        </div>
      </PageContentContainer>
    );
  } else {
    pageContent = (
      <PageContentContainer>
        <NoSearchResult
          message={NO_SEARCH_RESULT_MESSAGE}
          pastCarList={pastCarList}
          redirectRoute="/listing"
          title={NO_SEARCH_RESULT_TITLE}
          isSearchClearable />
      </PageContentContainer>
    );
  }

  const handleResultPerPageChange = async (value: number) => {
    setResultPerPage(value);

    const search = createQueryString({
      query: {
        ...defaultSearchData.query,
        page: undefined,
        limit: Number(value),
      },
    });

    setLeaderboardRenderKey(prev => prev + 1);
    router.push(`/listing/search?${search}`);
  };

  const handlePageChange = async (page: number) => {
    const searchData = { query: { ...defaultSearchData.query, page } };
    const search = createQueryString(searchData);

    setLeaderboardRenderKey(prev => prev + 1);
    router.push(`/listing/search?${search}`);
  };

  const handleSubmit = async (
    searchValue: string,
    makeValue: string,
    selectValues: Record<string, FilterSelect>,
    rangeValues: Record<string, FilterRange>,
    choiceValues: Record<string, FilterChoice>,
    checkboxValues: Record<string, Category<'checkbox'>
    & { values: Record<string, CheckboxValue>; }>,
  ) => {
    appContextData?.setIsFilterModalOpen(false);
    const searchData: OnSearchData = {
      query: {
        ...defaultSearchData.query,
        make: makeValue || undefined,
        q: searchValue || undefined,
        page: undefined,
        dealerId: undefined,
      },
    };

    Object.values(rangeValues).forEach((v) => {
      switch (v.name) {
        case 'Price Range':
          searchData.query.minPrice = +v.lowerValue.value || undefined;
          searchData.query.maxPrice = +v.upperValue.value || undefined;
          break;
        case 'Depreciation':
          searchData.query.minDepreciation = +v.lowerValue.value || undefined;
          searchData.query.maxDepreciation = +v.upperValue.value || undefined;
          break;
        case 'Drive Range':
          searchData.query.minDriveRange = +v.lowerValue.value || undefined;
          searchData.query.maxDriveRange = +v.upperValue.value || undefined;
          break;
        default:
          break;
      }
    });

    Object.values(choiceValues).forEach((v) => {
      switch (v.name) {
        case 'OMV':
          switch (v.optionValue.value) {
            case '>':
              if (v.inputValue) {
                searchData.query.minOmv = parseInt(v.inputValue, 10);
                searchData.query.maxOmv = undefined;
              }
              break;
            case '<':
              if (v.inputValue) {
                searchData.query.minOmv = undefined;
                searchData.query.maxOmv = parseInt(v.inputValue, 10);
              }
              break;
            case '=':
              if (v.inputValue) {
                searchData.query.minOmv = parseInt(v.inputValue, 10);
                searchData.query.maxOmv = parseInt(v.inputValue, 10);
              }
              break;
            default:
              searchData.query.minOmv = undefined;
              searchData.query.maxOmv = undefined;
              break;
          }
          break;
        case 'Fuel Consumption':
          switch (v.optionValue.value) {
            case '>':
              if (v.inputValue) {
                searchData.query.minFuelEconomy = parseInt(v.inputValue, 10);
                searchData.query.maxFuelEconomy = undefined;
              }
              break;
            case '<':
              if (v.inputValue) {
                searchData.query.minFuelEconomy = undefined;
                searchData.query.maxFuelEconomy = parseInt(v.inputValue, 10);
              }
              break;
            case '=':
              if (v.inputValue) {
                searchData.query.minFuelEconomy = parseInt(v.inputValue, 10);
                searchData.query.maxFuelEconomy = parseInt(v.inputValue, 10);
              }
              break;
            default:
              searchData.query.minFuelEconomy = undefined;
              searchData.query.maxFuelEconomy = undefined;
              break;
          }
          break;
        case 'Power Output':
          switch (v.optionValue.value) {
            case '>':
              if (v.inputValue) {
                searchData.query.minPowerOutput = parseInt(v.inputValue, 10);
                searchData.query.maxPowerOutput = undefined;
              }
              break;
            case '<':
              if (v.inputValue) {
                searchData.query.minPowerOutput = undefined;
                searchData.query.maxPowerOutput = parseInt(v.inputValue, 10);
              }
              break;
            case '=':
              if (v.inputValue) {
                searchData.query.minPowerOutput = parseInt(v.inputValue, 10);
                searchData.query.maxPowerOutput = parseInt(v.inputValue, 10);
              }
              break;
            default:
              searchData.query.minPowerOutput = undefined;
              searchData.query.maxPowerOutput = undefined;
              break;
          }
          break;
        case 'Energy Consumption':
          switch (v.optionValue.value) {
            case '>':
              if (v.inputValue) {
                searchData.query.minEnergyConsumption = parseInt(
                  v.inputValue,
                  10,
                );
                searchData.query.maxEnergyConsumption = undefined;
              }
              break;
            case '<':
              if (v.inputValue) {
                searchData.query.minEnergyConsumption = undefined;
                searchData.query.maxEnergyConsumption = parseInt(
                  v.inputValue,
                  10,
                );
              }
              break;
            case '=':
              if (v.inputValue) {
                searchData.query.minEnergyConsumption = parseInt(
                  v.inputValue,
                  10,
                );
                searchData.query.maxEnergyConsumption = parseInt(
                  v.inputValue,
                  10,
                );
              }
              break;
            default:
              searchData.query.minEnergyConsumption = undefined;
              searchData.query.maxEnergyConsumption = undefined;
              break;
          }
          break;
        default:
          break;
      }
    });

    Object.values(checkboxValues).forEach((checkbox) => {
      if (checkbox.name === 'Vehicle Type') {
        searchData.query.vehicleType = Object.values(checkbox.values)
          .filter(v => v.checked)
          .map(v => v.value)
          .join(',') || undefined;
      }
    });

    Object.values(selectValues).forEach((v) => {
      switch (v.name) {
        case 'Transmission':
          searchData.query.transmission = v.optionValue.value.toString()
          || undefined;
          break;
        case 'Fuel Type':
          if (isNewCarFuelType(v.optionValue.value)) {
            if (v.optionValue.value === 'electric') {
              searchData.query.fuelType = v.optionValue.value;
            } else {
              searchData.query.fuelType = v.optionValue.value;
              searchData.query.minDriveRange = undefined;
              searchData.query.maxDriveRange = undefined;
              searchData.query.minEnergyConsumption = undefined;
              searchData.query.maxEnergyConsumption = undefined;
            }
          } else {
            searchData.query.fuelType = undefined;
          }
          break;
        case 'Category':
          if (isCategoryType(v.optionValue.value)) {
            searchData.query.category = v.optionValue.value;
          } else {
            searchData.query.category = undefined;
            searchData.query.coeCategory = undefined;
          }
          break;
        case 'COE Type':
          if (isCategoryType(v.optionValue.value)) {
            searchData.query.coeCategory = v.optionValue.value;
          } else {
            searchData.query.coeCategory = undefined;
          }
          break;
        case 'Dealer Type':
          if (v.optionValue.value === AUTHORISED_DISTRIBUTORS
            || v.optionValue.value === PARALLEL_IMPORTERS) {
            searchData.query.dealerType = v.optionValue.value;
          } else {
            searchData.query.dealerType = undefined;
          }
          break;
        case 'Country':
          searchData.query.country = v.optionValue.value.toString()
          || undefined;
          break;
        case 'Engine Cap':
          switch (v.optionValue.value) {
            case ENGINE_CAP_SELECT_OPTION_VALUE_LESS_THAN_661:
              searchData.query.minEngineCapacity = undefined;
              searchData.query.maxEngineCapacity = 661;
              break;
            case ENGINE_CAP_SELECT_OPTION_VALUE_661_TO_1600:
              searchData.query.minEngineCapacity = 661;
              searchData.query.maxEngineCapacity = 1600;
              break;
            case ENGINE_CAP_SELECT_OPTION_VALUE_1601_TO_2000:
              searchData.query.minEngineCapacity = 1601;
              searchData.query.maxEngineCapacity = 2000;
              break;
            case ENGINE_CAP_SELECT_OPTION_VALUE_2001_TO_3000:
              searchData.query.minEngineCapacity = 2001;
              searchData.query.maxEngineCapacity = 3000;
              break;
            case ENGINE_CAP_SELECT_OPTION_VALUE_MORE_THAN_3000:
              searchData.query.minEngineCapacity = 3000;
              searchData.query.maxEngineCapacity = undefined;
              break;
            default:
              searchData.query.minEngineCapacity = undefined;
              searchData.query.maxEngineCapacity = undefined;
              break;
          }
          break;
        default:
          break;
      }
    });

    const search = createQueryString(searchData);

    setLeaderboardRenderKey(prev => prev + 1);
    router.push(`/listing/search?${search}`);
  };

  const handleLabelRemoveAll = async () => {
    const searchData: OnSearchData = {
      query: {
        q: defaultSearchData.query.q,
        sortBy: defaultSearchData.query.sortBy,
        order: defaultSearchData.query.order,
        page: undefined,
      },
    };
    const search = createQueryString(searchData);

    setLeaderboardRenderKey(prev => prev + 1);
    router.push(`/listing/search?${search}`);
  };

  const handleLabelRemove = async (index: number, value: FilterValue) => {
    const searchData: OnSearchData = {
      query: {
        ...defaultSearchData.query,
      },
    };

    switch (value.name) {
      case 'Car Make':
        delete searchData.query.make;
        break;
      case 'Transmission':
        delete searchData.query.transmission;
        break;
      case 'Fuel Type':
        delete searchData.query.fuelType;
        break;
      case 'Category':
        delete searchData.query.category;
        break;
      case 'Dealer Type':
        delete searchData.query.dealerType;
        break;
      case 'COE Type':
        delete searchData.query.coeCategory;
        break;
      case 'Country':
        delete searchData.query.country;
        break;
      case 'Engine Cap':
        delete searchData.query.minEngineCapacity;
        delete searchData.query.maxEngineCapacity;
        break;
      case 'Price Range':
        delete searchData.query.minPrice;
        delete searchData.query.maxPrice;
        break;
      case 'Depreciation':
        delete searchData.query.minDepreciation;
        delete searchData.query.maxDepreciation;
        break;
      case 'Drive Range':
        delete searchData.query.minDriveRange;
        delete searchData.query.maxDriveRange;
        break;
      case 'OMV':
        delete searchData.query.minOmv;
        delete searchData.query.maxOmv;
        break;
      case 'Fuel Consumption':
        delete searchData.query.minFuelEconomy;
        delete searchData.query.maxFuelEconomy;
        break;
      case 'Energy Consumption':
        delete searchData.query.minEnergyConsumption;
        delete searchData.query.maxEnergyConsumption;
        break;
      case 'Power Output':
        delete searchData.query.minPowerOutput;
        delete searchData.query.maxPowerOutput;
        break;
      case 'Vehicle Type':
        if (isFilterCheckbox(value)) {
          if (searchData.query.vehicleType?.includes(',')) {
            const vehicleType = searchData.query.vehicleType
              .split(',')
              .filter(v => value.checkboxValue !== v)
              .join(',');

            searchData.query.vehicleType = vehicleType;
          } else {
            delete searchData.query.vehicleType;
          }
        }
        break;
      default:
        break;
    }

    const search = createQueryString(searchData);

    setLeaderboardRenderKey(prev => prev + 1);
    router.push(`/listing/search?${search}`);
  };

  const handleAdAlertModalSubmit = (
    alert: CamelCaseAdAlert | CamelCaseCarAlert,
  ) => {
    if (isCamelCaseAdAlert(alert) && myAlert) {
      // remove
      if (!alert.adCarLaunches && !alert.adPromo) {
        const newAdAlert = myAlert?.adAlerts
          .filter(ad => ad.make !== alert.make);

        if (newAdAlert) {
          setMyAlert({ ...myAlert, adAlerts: newAdAlert });
          setAdAlert(alert);
        }
      } else { // add
        setMyAlert({ ...myAlert, adAlerts: [...myAlert.adAlerts, alert] });
        setAdAlert(alert);
      }
    }
  };

  const handleCompareItemRemove = async (submodelId: number) => {
    const formData = new FormData();
    const newCompareItems = compareItems.filter(
      item => item.submodelId !== submodelId,
    );

    formData.set(
      'submodelIds',
      newCompareItems
        .map(item => item.submodelId)
        .join(','),
    );

    formData.set(
      'modelIds',
      newCompareItems
        .map(item => item.id)
        .join(','),
    );

    if (newCompareItems.length === 0) {
      setIsComparisonDrawerOpen(false);
    }
    setCompareItems(newCompareItems);

    await setNewcarItemsCookies(formData);
  };

  const handleCompareClearAllClick = async () => {
    const formData = new FormData();

    formData.set('submodelIds', '');
    formData.set('modelIds', '');

    setIsComparisonDrawerOpen(false);
    setCompareItems([]);
    setIsComparisonDrawerOpen(false);
    await setNewcarItemsCookies(formData);
  };

  const handleDrawerToggleClick = () => {
    setIsComparisonDrawerOpen(prev => !prev);
  };

  return (
    <>
      <DynamicEyeotaScript
        pid={eyeotaPid ?? ''}
        targetMapping={
          createEyeotaAJSScriptTargetMapping(defaultSearchData.query)
        } />
      <main
        className={cx(
          styles.containerRoot,
          !hasSearchResult && styles.hasNoSearchResult,
        )}>
        <PageContentContainer
          className={cx(
            styles.pageContainerRoot,
            styles.pageContainerRootOverride,
          )}>
          <Leaderboard
            className={styles.containerLeaderboard}
            dyAdsConfig={dynamicYieldsListingLeaderboard}
            googleAdsConfig={{
              ...googleAdsListingLeaderboard,
              targetingMap: searchTargetMapping,
            }}
            googleAdsProbability={0.8}
            renderKey={leaderboardRenderKey} />
          <div
            className={styles.containerBreadcrumb}>
            <Breadcrumb
              list={breadcrumbList} />
          </div>
          <div
            ref={containerSearchFilterSectionRef}
            className={styles.containerSearchFilter}>
            <SearchFilterSection
              brandList={brandList}
              carMakeValue={carMakeValue}
              defaultSearchData={defaultSearchData}
              isFilterModalOpen={appContextData?.isFilterModalOpen ?? false}
              searchBarClassName={cx(
                styles.containerSearchBar,
                styles.containerSearchBarOverride,
              )}
              searchValue={query}
              setCarMakeValue={setCarMakeValue}
              setSearchValue={setQuery}
              submitButtonOption={{
                disabled: isSearchPending,
                variant: 'contained',
              }}
              onFilterClick={handleFilterClick}
              onFilterModalClose={
                () => appContextData?.setIsFilterModalOpen(false)
              }
              onLabelRemove={handleLabelRemove}
              onLabelRemoveAll={handleLabelRemoveAll}
              onModalSubmit={handleSubmit}
              onSubmit={handleSubmit} />
          </div>
          <div className={styles.containerPage}>
            {dealerDetail && dealerList && (
              <div className={styles.containerAdInfoList}>
                <AdInfoList
                  adAlerts={adAlert || {
                    make: dealerDetail.brand ?? '',
                    adPromo: false,
                    adCarLaunches: false,
                  }}
                  adInfoList={dealerDetail}
                  adMenuOptions={dealerList}
                  onAlertModalSubmit={handleAdAlertModalSubmit} />
              </div>
            )}
            {isScreenDesktop
            && carMakeList
            && !defaultSearchData.query.dealerId
            && (
              <div className={styles.containerCarMakeInfoList}>
                <CarMakeInfoList list={carMakeList} />
              </div>
            )}
            <div className={styles.containerSearchResult}>
              {(isSearchPending || hasSearchResult || !isScreenDesktop) && (
                <>
                  <div className={styles.containerTitle}>
                    <h1 className={styles.textH1}>
                      New
                      {defaultSearchData.query.q && brandList.findIndex(
                        v => v
                          .toLowerCase() === defaultSearchData.query.q
                          ?.toLowerCase(),
                      ) !== -1
                        ? ` ${brandList.find(
                          v => v
                            .toLowerCase() === defaultSearchData.query.q
                            ?.toLowerCase(),
                        )}`
                        : ''}
                      {defaultSearchData.query.make && !defaultSearchData.query.q ? ` ${defaultSearchData.query.make}` : ''}
                      {' '}
                      Cars For Sale
                    </h1>
                    <div className={styles.containerSortSelect}>
                      <SortSelect
                        sorter={sorter}
                        onSortChange={handleSortChange} />
                    </div>
                  </div>
                  <SortBar
                    isSearchPending={isSearchPending}
                    numberOfCars={numOfTotalCars}
                    numberOfFilter={filterValues.length}
                    sorter={sorter}
                    isTopRounded
                    onFilterClick={handleFilterClick}
                    onSortChange={handleSortChange} />
                </>
              )}
              {pageContent}
            </div>
            {hasSearchResult && lastPage !== 1 && (
              <div className={styles.containerPagination}>
                <div className={styles.pagination}>
                  {currentPage && lastPage && (
                    <Pagination
                      totalPage={lastPage}
                      value={currentPage}
                      onPageChange={handlePageChange} />
                  )}
                </div>
                <div className={styles.resultPerPageDropdown}>
                  <ResultPerPageDropdown
                    value={resultPerPage}
                    onChange={handleResultPerPageChange} />
                  <div className={styles.textResultAndPage}>results/page</div>
                </div>
              </div>
            )}
            {hasPastCarResult && (
              <div className={styles.containerPastCar}>
                <SectionContainer
                  contentWidth="full"
                  description={(
                    <PastCarDescription
                      searchInput={defaultSearchData.query.q}
                      total={numOfPastCarsTotal} />
                  )}
                  title={SECTION_TITLE_NEW_CARS_LISTING_PAST_CARS}
                  titleAsideContent={(
                    <TextLink
                      className={styles.linkViewAll}
                      href={`${joinSgcmMsSite(SITE_BASE_PATH ?? '')}/past-cars`}
                      text="View All" />
                  )}
                  titleHeading={TITLE_HEADING_H3}
                  variant={SECTION_CONTAINER_VARIANT_CONTENT_OVERFLOWED}>
                  {null}
                </SectionContainer>
                <PastCarList
                  items={pastCarList} />
              </div>
            )}
          </div>
        </PageContentContainer>
      </main>
      {isScreenDesktop && (
        <div className={styles.containerComparisonDrawer}>
          <ComparisonDrawer
            isOpen={isComparisonDrawerOpen}
            isWarningModalOpen={isComparisonWarningModalOpen}
            items={compareItems}
            onClearAllBtnClick={handleCompareClearAllClick}
            onCloseClick={handleCompareItemRemove}
            onDrawerToggleClick={handleDrawerToggleClick}
            onWarningModalCloseClick={
              () => setIsComparisonWarningModalOpen(false)
            } />
        </div>
      )}
    </>
  );
};
